import { useState } from 'react';

import { MIXPANEL_EVENT_NAMES } from 'constants/mixpanelEventNames';
import { trackMixpanelEvent } from 'helpers/mixpanel-init';
import useTheme from 'hooks/useTheme';
import { ChevronRight } from 'styles/assets/icons';
import ArrowNext from 'styles/assets/icons/ArrowNext';
import ArrowPrevious from 'styles/assets/icons/ArrowPrevious';
import CrossIcon from 'styles/assets/icons/CloseIcon';
import TickRound from 'styles/assets/icons/TickRound';
import MaterialConfirmDialog from 'UIKit/MaterialConfirmDialog';

import styles from './introSlides.module.scss';

const straddleIntroAssetsFolderUrl = 'https://static.delta.exchange/web/straddle';
const straddleIntroButtonTitle = 'What are Straddles';

const getSlidesContent = (isLightTheme: boolean) => {
  const themeSuffix = isLightTheme ? 'light' : 'dark';
  return [
    {
      title: 'What is a Straddle?',
      bulletPoints: [
        'A call + put option bundled at the same strike',
        'Simple way to trade BTC/ETH volatility on daily expiries',
      ],
      svgComp: (
        <img
          src={`${straddleIntroAssetsFolderUrl}/straddle-intro-slide-1.svg`}
          alt="Straddle Intro Slide 1"
          data-palette="getSlidesContent" />
      ),
    },
    {
      title: 'Why choose Straddles?',
      bulletPoints: [
        '50% lower fees - charged only on one leg',
        'Lower slippage - better liquidity & tighter spreads',
        'Faster execution - trade 2 options with 1 order',
      ],
      svgComp: (
        <img
          src={`${straddleIntroAssetsFolderUrl}/straddle-intro-slide-2-${themeSuffix}.svg`}
          alt="Straddle Intro Slide 2"
          data-palette="getSlidesContent" />
      ),
    },
    {
      title: 'What stays the same?',
      bulletPoints: [
        'Enjoy the same leverage and all other F&O features',
        'Trade directly in INR without the need of any conversions',
      ],
      svgComp: (
        <img
          src={`${straddleIntroAssetsFolderUrl}/straddle-intro-slide-3-${themeSuffix}.svg`}
          alt="Straddle Intro Slide 3"
          data-palette="getSlidesContent" />
      ),
    },
  ];
};

const StraddleIntroSlides = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => {
  const { isLightTheme } = useTheme();
  const allSlides = getSlidesContent(isLightTheme);
  const [slideCount, setSlideCount] = useState(1);
  const currentSlideContent = allSlides[slideCount - 1];

  const handlePrevious = () => {
    setSlideCount(slideCount - 1);
  };

  const handleNext = () => {
    setSlideCount(slideCount + 1);
    if (slideCount === allSlides.length) {
      onClose();
      setSlideCount(1);
    }
  };

  return (
    (<MaterialConfirmDialog
      open={open}
      onClose={onClose}
      showHeader={false}
      showFooter={false}
      containerClassName={styles.introModal}
      bodyClassName={styles.introModalBody}
      rootClassName={styles.introRoot}
      contentClassName={styles.introContent}
    >
      <div className={styles.introSlides}>
        <p className={styles.title}>{currentSlideContent.title}</p>
        <button
          type="button"
          data-testid="introSlidesCloseButton"
          className={styles.closeIcon}
          onClick={onClose}
        >
          <CrossIcon color="var(--main-text-secondary)" />
        </button>
        <div className={styles.image}>{currentSlideContent.svgComp}</div>
        <div className={styles.bulletPoints}>
          {currentSlideContent.bulletPoints.map(point => {
            return (
              (<div className={styles.bulletPoint} data-palette="StraddleIntroSlides">
                <TickRound className={styles.tickIcon} />
                <p>{point}</p>
              </div>)
            );
          })}
        </div>
        <div className={styles.buttons}>
          {slideCount > 1 && (
            <button
              data-testid="introSlidesPreviousButton"
              onClick={handlePrevious}
              disabled={slideCount === 1}
              className={styles.previousButton}
              type="button"
              data-palette="StraddleIntroSlides">
              <ArrowPrevious /> Previous
            </button>
          )}
          <button
            data-testid="introSlidesNextButton"
            onClick={handleNext}
            className={styles.nextButton}
            type="button"
          >
            {slideCount === allSlides.length ? 'Finish' : 'Next'} <ArrowNext />
          </button>
        </div>
      </div>
    </MaterialConfirmDialog>)
  );
};

const StraddleIntro = () => {
  const [showStraddleIntro, setShowStraddleIntro] = useState(false);

  const openModal = () => {
    trackMixpanelEvent(MIXPANEL_EVENT_NAMES.STRADDLE_INTRO_CLICKED);
    setShowStraddleIntro(true);
  };

  const closeModal = () => {
    setShowStraddleIntro(false);
  };

  return (<>
    <div className={styles.buttonContainer} data-palette="StraddleIntro">
      <button
        type="button"
        data-testid="open-intro-button"
        className={styles.button}
        onClick={openModal}
      >
        {straddleIntroButtonTitle} <ChevronRight />
      </button>
    </div>
    <StraddleIntroSlides open={showStraddleIntro} onClose={closeModal} />
  </>);
};

export default StraddleIntro;
